/**
 * Adresse de retour après connexion (`/connexion?retour=…`).
 *
 * React Router traite `//hote/…` ou `https://hote/…` comme une navigation
 * externe : un `retour` non contrôlé ouvrirait une redirection vers un autre
 * site après la connexion. Seul un chemin relatif de l'application est
 * accepté : un seul `/` en tête, ni `//`, ni schéma, ni `\` (que les
 * navigateurs lisent comme `/`), ni caractère de contrôle. Tout le reste
 * renvoie à l'accueil. La destination reconstruite par `URL` est contrôlée à
 * son tour : la normalisation de `/a/..//hote/x` ou de `/%2e%2e//hote/x`
 * redonne `//hote/x`.
 */
const ORIGINE_FICTIVE = 'https://regie.invalid';

export function retourSur(valeur: string | null | undefined): string {
  if (!valeur || !valeur.startsWith('/') || valeur.startsWith('//')) return '/';
  // eslint-disable-next-line no-control-regex -- refuser précisément les caractères de contrôle
  if (valeur.includes('\\') || /[\u0000-\u001f\u007f]/.test(valeur)) return '/';
  try {
    const url = new URL(valeur, ORIGINE_FICTIVE);
    if (url.origin !== ORIGINE_FICTIVE) return '/';
    if (url.pathname.startsWith('//') || url.pathname.includes('\\')) return '/';
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return '/';
  }
}

/**
 * Le routeur retire le fragment de l'URL passée aux chargeurs : on le reprend
 * de la fenêtre seulement quand elle affiche bien cette adresse (premier
 * chargement), jamais lors d'une navigation où elle montre encore l'ancienne
 * page.
 */
function fragmentAffiche(chemin: string): string {
  const fenetre = typeof window === 'undefined' ? null : window.location;
  return fenetre && `${fenetre.pathname}${fenetre.search}` === chemin ? fenetre.hash : '';
}

/**
 * Chemin, paramètres et fragment de la page demandée, pour `retour`.
 * Limite connue : si la session expire pendant une navigation interne, le
 * fragment de la page visée est perdu (la fenêtre montre encore l'ancienne).
 */
export function adresseDeRetour(urlDemandee: string): string {
  const url = new URL(urlDemandee);
  const chemin = `${url.pathname}${url.search}`;
  return retourSur(`${chemin}${fragmentAffiche(chemin)}`);
}

/**
 * Paramètres et fragment de la page demandée, à reporter sur une redirection
 * interne (`/` → `/<galerie>`), avec la même règle pour le fragment.
 */
export function parametresEtFragment(urlDemandee: string): string {
  const url = new URL(urlDemandee);
  return `${url.search}${fragmentAffiche(`${url.pathname}${url.search}`)}`;
}
